<template>
  <div class="help-root">
    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">shield_lock</span>
      <p>
        La autenticación en dos pasos (2FA) protege tu cuenta con un código
        temporal generado en tu teléfono. Aquí te explicamos cómo configurarla
        usando Google Authenticator, Microsoft Authenticator o Authy.
      </p>
    </div>

    <!-- Paso inicial común -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">qr_code_scanner</span>
        Antes de empezar
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in pasoInicial" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Google Authenticator -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">android</span>
        Google Authenticator
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="(paso, i) in googleAuth" :key="paso.titulo">
          <span class="help-step-number">{{ i + 1 }}</span>
          <div>
            <p class="help-card-title">{{ paso.titulo }}</p>
            <p class="help-card-desc">{{ paso.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Microsoft Authenticator -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">window</span>
        Microsoft Authenticator
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="(paso, i) in microsoftAuth" :key="paso.titulo">
          <span class="help-step-number">{{ i + 1 }}</span>
          <div>
            <p class="help-card-title">{{ paso.titulo }}</p>
            <p class="help-card-desc">{{ paso.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Authy -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">verified_user</span>
        Authy
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="(paso, i) in authy" :key="paso.titulo">
          <span class="help-step-number">{{ i + 1 }}</span>
          <div>
            <p class="help-card-title">{{ paso.titulo }}</p>
            <p class="help-card-desc">{{ paso.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Comparativa rápida -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">compare_arrows</span>
        Diferencias clave
      </h3>
      <div class="help-estados">
        <span v-for="d in diferencias" :key="d.label" class="help-estado-badge"
          :style="{ background: d.color, color: d.text }">{{ d.label }}</span>
      </div>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>Guarda siempre los códigos de respaldo que te entrega la aplicación al activar el 2FA: te permitirán recuperar el acceso si pierdes tu teléfono.</p>
    </div>
  </div>
</template>
<script>
export default {
  name: "AyudaAutenticadorForm",
  data() {
    return {
      pasoInicial: [
        { icon: "settings",  titulo: "Activa el 2FA en tu cuenta", desc: "Ingresa a la configuración de seguridad del sistema y selecciona la opción para activar la verificación en dos pasos." },
        { icon: "qr_code_2", titulo: "Aparecerá un código QR",     desc: "El sistema mostrará un código QR (o una clave alfanumérica) que deberás escanear con tu app de autenticación." },
        { icon: "download",  titulo: "Instala una app autenticadora", desc: "Descarga Google Authenticator, Microsoft Authenticator o Authy desde la tienda de aplicaciones de tu teléfono (App Store o Google Play)." },
      ],
      googleAuth: [
        { titulo: "Abre la app y pulsa +", desc: "En la pantalla principal, toca el botón con el signo más (+) para agregar una nueva cuenta." },
        { titulo: "Elige 'Escanear código QR'", desc: "Selecciona esta opción y apunta la cámara al código QR mostrado por el sistema." },
        { titulo: "Confirma el código de 6 dígitos", desc: "La app generará un código numérico que cambia cada 30 segundos; ingrésalo en el sistema para finalizar la configuración." },
        { titulo: "Guarda la cuenta", desc: "Verás la cuenta agregada en la lista de Google Authenticator, lista para generar códigos cada vez que inicies sesión." },
      ],
      microsoftAuth: [
        { titulo: "Abre la app y toca 'Agregar cuenta'", desc: "En la pantalla principal selecciona el ícono + y elige el tipo de cuenta 'Otra (Google, Facebook, etc.)'." },
        { titulo: "Escanea el código QR", desc: "Usa la cámara para escanear el código QR que muestra el sistema; también puedes ingresar la clave manualmente." },
        { titulo: "Ingresa el código generado", desc: "Copia el código de 6 dígitos que aparece en la app y pégalo en el sistema para validar la vinculación." },
        { titulo: "Activa notificaciones (opcional)", desc: "Si el sistema lo soporta, Microsoft Authenticator también permite aprobar inicios de sesión con una notificación push, sin necesidad de escribir el código." },
      ],
      authy: [
        { titulo: "Crea tu cuenta en Authy", desc: "Al instalar la app por primera vez, regístrate con tu número de teléfono; Authy lo usará para respaldar tus cuentas en la nube." },
        { titulo: "Pulsa el botón + para añadir", desc: "Selecciona 'Agregar cuenta' y escanea el código QR mostrado por el sistema." },
        { titulo: "Verifica el código generado", desc: "Ingresa el código de 6 dígitos en el sistema para completar la activación del 2FA." },
        { titulo: "Sincroniza en varios dispositivos", desc: "A diferencia de Google y Microsoft Authenticator, Authy permite tener tus cuentas sincronizadas y respaldadas en la nube, accesibles desde varios dispositivos." },
      ],
      diferencias: [
        { label: "Google Authenticator: sin respaldo en la nube", color: "#f8d7da", text: "#721c24" },
        { label: "Microsoft Authenticator: soporta notificaciones push", color: "#d1ecf1", text: "#0c5460" },
        { label: "Authy: respaldo multi-dispositivo en la nube", color: "#d4edda", text: "#155724" },
      ],
    };
  },
};
</script>
<style src="@/assets/css/ayuda.css" scoped></style>